<template>
	<div v-if="myCustomer?.id === 4">
		<div
			class="mx-auto mb-4 mt-4 w-[85%] w-full rounded-md border-[1px] border-b-gray-200"
		/>

		<div class="mt-2 flex flex-col gap-2">
			<div class="flex w-full flex-row gap-2">
				<div class="flex-1">
					<sg-select-modal
						v-model:multi="selectedWeedTypes"
						label="Weed Types"
						:items="weedTypes"
						placeholder="Select"
					/>
				</div>
				<div class="flex-1">
					<sg-select-modal
						v-model:single="selectedWeedPressure"
						label="Weed Pressure"
						:items="weedPressures"
						placeholder="Select"
						:unselectable="false"
					/>
				</div>
			</div>

			<div class="flex w-full flex-row gap-2">
				<div class="flex-1 px-1 py-3">
					<ion-checkbox v-model="largeWeeds">
						Large Weeds
					</ion-checkbox>
				</div>
				<div class="flex-1 px-1 py-3">
					<ion-checkbox v-model="weedAll">Weed All</ion-checkbox>
				</div>
			</div>

			<div class="flex w-full flex-row gap-2">
				<div class="flex-1">
					<sg-select-modal
						v-model:single="selectedJobTool"
						label="Job Tool"
						:items="jobTools"
						placeholder="Select"
					/>
				</div>
				<div class="flex-1">
					<sg-select-modal
						v-model:multi="selectedObservations"
						label="Observations"
						:items="observations"
						placeholder="Select"
					/>
				</div>
			</div>

			<sg-textarea
				v-model="observationComments"
				label="Observation Comments"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { useMyCustomer } from "seedgreen-shared/queries/customer";
	import SgSelectModal from "@/components/ui/modals/SgSelectModal.vue";
	import { ref, computed, watch } from "vue";
	import SgTextarea from "@/components/ui/SgTextarea.vue";
	import { IonCheckbox } from "@ionic/vue";
	import type { BonipakWeedFieldData } from "seedgreen-shared/dtos/TaskRequestDTO";

	// Use defineModel for cleaner v-model handling
	const modelData = defineModel<BonipakWeedFieldData>();

	const weedTypes = [
		"Chickweed",
		"Crabgrass",
		"Dandelion",
		"Malva",
		"Milk Thistle",
		"Nutgrass Weed",
		"Pigweed",
		"Purslane",
		"Shepards Purse",
		"Stinging Nettle",
	]
		.toSorted()
		.map((s) => ({ id: s, text: s }));

	const weedPressures = ["Low", "Moderate", "High"].map((s) => ({
		id: s,
		text: s,
	}));

	const jobTools = ["Hand", "Hoe"].map((s) => ({ id: s, text: s }));

	const observations = [
		"Tape",
		"Doubles and Weeds",
		"Edges & Ovals",
		"INSV",
		"Other",
	].map((s) => ({ id: s, text: s }));

	// Form data refs - initialize with model values if provided
	const selectedWeedTypes = ref<Set<string>>(
		new Set(modelData.value?.weedTypes || []),
	);
	const selectedWeedPressure = ref<string | undefined>(
		modelData.value?.pressure || "Low",
	);
	const largeWeeds = ref(modelData.value?.largeWeeds || false);
	const weedAll = ref(modelData.value?.weedAll || false);
	const selectedJobTool = ref<string | undefined>(
		modelData.value?.jobTool || "Hand",
	);
	const selectedObservations = ref<Set<string>>(
		new Set(modelData.value?.observations || []),
	);
	const observationComments = ref(modelData.value?.observationComments || "");

	// Computed property to build the complete BonipakWeedFieldData object
	const formDataObject = computed<BonipakWeedFieldData>(() => ({
		weedTypes: Array.from(selectedWeedTypes.value),
		pressure: selectedWeedPressure.value,
		largeWeeds: largeWeeds.value,
		weedAll: weedAll.value,
		jobTool: selectedJobTool.value,
		observations: Array.from(selectedObservations.value),
		observationComments: observationComments.value || undefined,
	}));

	// Watch for changes and update the model
	watch(
		formDataObject,
		(newValue) => {
			modelData.value = newValue;
		},
		{ deep: true, immediate: true },
	);

	const { myCustomer } = useMyCustomer();
</script>
